import { useCallback, useEffect, useRef, useState } from 'react';
import type { EmployeeView } from '../../../services/employees/employees.service';
import { employeesService } from '../../../services/employees/employees.service';
import type { AreaView } from '../../../services/areas/areas.service';
import { areasService } from '../../../services/areas/areas.service';
import type { ProductView } from '../../../services/products/products.service';
import { productsService } from '../../../services/products/products.service';
import type { PrintJobView, PrinterView } from '../../../services/printers/printers.service';
import { printersService } from '../../../services/printers/printers.service';
import { subscribeSharedTicker } from '../../../utils/shared-ticker';
import { normalizeArea } from '../core/pos-shell-page.helpers';

type UsePosSettingsBootstrapParams = {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  isSettingsActive: boolean;
  settingsModalKey: string | null;
  activePrinters: PrinterView[];
  setError: (message: string | null) => void;
  setSettingsEmployees: React.Dispatch<React.SetStateAction<EmployeeView[]>>;
  setSettingsAreas: React.Dispatch<React.SetStateAction<AreaView[]>>;
  setSelectedAreaId: React.Dispatch<React.SetStateAction<number | null>>;
  setIsLoadingSettings: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingAreaSettings: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingProducts: React.Dispatch<React.SetStateAction<boolean>>;
  setSettingsProducts: React.Dispatch<React.SetStateAction<ProductView[]>>;
  setIsLoadingPrinters: React.Dispatch<React.SetStateAction<boolean>>;
  setActivePrinters: React.Dispatch<React.SetStateAction<PrinterView[]>>;
  setIsLoadingPrintJobs: React.Dispatch<React.SetStateAction<boolean>>;
  setPrintJobs: React.Dispatch<React.SetStateAction<PrintJobView[]>>;
};

/**
 * Architectural summary: Owns manager settings bootstrap logic (one-shot
 * loads, print-jobs live stream, printer selection defaulting).
 */
export function usePosSettingsBootstrap({
  tenantId,
  employeeSession,
  isManager,
  isSettingsActive,
  settingsModalKey,
  activePrinters,
  setError,
  setSettingsEmployees,
  setSettingsAreas,
  setSelectedAreaId,
  setIsLoadingSettings,
  setIsLoadingAreaSettings,
  setIsLoadingProducts,
  setSettingsProducts,
  setIsLoadingPrinters,
  setActivePrinters,
  setIsLoadingPrintJobs,
  setPrintJobs,
}: UsePosSettingsBootstrapParams) {
  const [selectedTestPrinterId, setSelectedTestPrinterId] = useState<number | null>(null);
  const [hasEverOpenedPrinterSettings, setHasEverOpenedPrinterSettings] = useState(false);
  const printJobsFallbackUnsubscribeRef = useRef<(() => void) | null>(null);
  const settingsInitialLoadForSessionRef = useRef<string | null>(null);
  const printersLoadedForSessionRef = useRef<string | null>(null);

  const clearPrintJobsFallback = useCallback(() => {
    printJobsFallbackUnsubscribeRef.current?.();
    printJobsFallbackUnsubscribeRef.current = null;
  }, []);

  const loadSettingsEmployees = useCallback(async () => {
    if (!isManager || !employeeSession) {
      return;
    }

    setIsLoadingSettings(true);
    setError(null);
    try {
      const list = await employeesService.listForSettings(tenantId, employeeSession);
      setSettingsEmployees(list);
    } catch (settingsError: unknown) {
      if (settingsError instanceof Error) {
        setError(settingsError.message);
      } else {
        setError('Failed loading employee settings');
      }
    } finally {
      setIsLoadingSettings(false);
    }
  }, [employeeSession, isManager, setError, setIsLoadingSettings, setSettingsEmployees, tenantId]);

  const loadSettingsAreas = useCallback(async () => {
    if (!isManager || !employeeSession) {
      return;
    }

    setIsLoadingAreaSettings(true);
    setError(null);
    try {
      const list = (await areasService.listForSettings(tenantId, employeeSession)).map((area) => normalizeArea(area));
      setSettingsAreas(list);
      setSelectedAreaId((previous) => {
        if (previous !== null && list.some((area) => area.id === previous)) {
          return previous;
        }
        return list[0]?.id ?? null;
      });
    } catch (settingsError: unknown) {
      if (settingsError instanceof Error) {
        setError(settingsError.message);
      } else {
        setError('Failed loading area settings');
      }
    } finally {
      setIsLoadingAreaSettings(false);
    }
  }, [employeeSession, isManager, setError, setIsLoadingAreaSettings, setSelectedAreaId, setSettingsAreas, tenantId]);

  const loadSettingsProducts = useCallback(async () => {
    if (!isManager || !employeeSession) {
      return;
    }

    setIsLoadingProducts(true);
    setError(null);
    try {
      const products = await productsService.listForSettings(tenantId, employeeSession);
      setSettingsProducts(products);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading products');
      }
    } finally {
      setIsLoadingProducts(false);
    }
  }, [employeeSession, isManager, setError, setIsLoadingProducts, setSettingsProducts, tenantId]);

  // Every role reads printers (POS general-item / print pickers); the endpoint is employee-scoped.
  const loadActivePrinters = useCallback(async () => {
    if (!employeeSession) {
      return;
    }

    setIsLoadingPrinters(true);
    setError(null);
    try {
      const printers = await printersService.listActive(tenantId, employeeSession);
      setActivePrinters(printers);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading printers');
      }
    } finally {
      setIsLoadingPrinters(false);
    }
  }, [employeeSession, setActivePrinters, setError, setIsLoadingPrinters, tenantId]);

  const loadPrintJobs = useCallback(async (options?: { silent?: boolean }) => {
    if (!isManager || !employeeSession) {
      return;
    }

    const silent = Boolean(options?.silent);
    if (!silent) {
      setIsLoadingPrintJobs(true);
    }
    setError(null);
    try {
      const jobs = await printersService.listPrintJobs(tenantId, employeeSession, 25);
      setPrintJobs(jobs);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading print jobs');
      }
    } finally {
      if (!silent) {
        setIsLoadingPrintJobs(false);
      }
    }
  }, [employeeSession, isManager, setError, setIsLoadingPrintJobs, setPrintJobs, tenantId]);

  const ensurePrintJobsFallback = useCallback(() => {
    if (printJobsFallbackUnsubscribeRef.current) {
      return;
    }

    printJobsFallbackUnsubscribeRef.current = subscribeSharedTicker(5000, () => {
      void loadPrintJobs({ silent: true });
    });
  }, [loadPrintJobs]);

  const applyStreamedPrintJob = useCallback((incoming: PrintJobView) => {
    setPrintJobs((previous) => {
      const withoutCurrent = previous.filter((job) => job.id !== incoming.id);
      return [incoming, ...withoutCurrent]
        .sort((a, b) => {
          const timeDelta = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          if (timeDelta !== 0) {
            return timeDelta;
          }
          return b.id - a.id;
        })
        .slice(0, 25);
    });
  }, [setPrintJobs]);

  useEffect(() => {
    setSelectedTestPrinterId((previous) => {
      if (previous !== null && activePrinters.some((printer) => printer.id === previous)) {
        return previous;
      }
      return activePrinters[0]?.id ?? null;
    });
  }, [activePrinters]);

  // Order entry needs printers before the hamburger is ever opened, so load them on session start.
  useEffect(() => {
    if (!employeeSession || printersLoadedForSessionRef.current === employeeSession) {
      return;
    }
    printersLoadedForSessionRef.current = employeeSession;
    void loadActivePrinters();
  }, [employeeSession, loadActivePrinters]);

  useEffect(() => {
    if (!isSettingsActive || !isManager || !employeeSession) {
      return;
    }
    if (settingsInitialLoadForSessionRef.current === employeeSession) {
      return;
    }
    settingsInitialLoadForSessionRef.current = employeeSession;

    void Promise.allSettled([
      loadSettingsEmployees(),
      loadSettingsProducts(),
      loadActivePrinters(),
      settingsModalKey === 'printers' ? loadPrintJobs() : Promise.resolve(),
    ]);
  }, [
    employeeSession,
    isManager,
    isSettingsActive,
    settingsModalKey,
    loadActivePrinters,
    loadPrintJobs,
    loadSettingsEmployees,
    loadSettingsProducts,
  ]);

  useEffect(() => {
    if (settingsModalKey === 'printers' && !hasEverOpenedPrinterSettings) {
      setHasEverOpenedPrinterSettings(true);
    }
  }, [settingsModalKey, hasEverOpenedPrinterSettings]);

  useEffect(() => {
    if (!employeeSession) {
      setHasEverOpenedPrinterSettings(false);
      settingsInitialLoadForSessionRef.current = null;
    }
  }, [employeeSession]);

  useEffect(() => {
    if (!hasEverOpenedPrinterSettings || !isManager || !employeeSession) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      ensurePrintJobsFallback();
      return () => {
        clearPrintJobsFallback();
      };
    }

    const unsubscribe = printersService.subscribePrintJobs(
      tenantId,
      employeeSession,
      (event) => {
        if (event.type === 'upsert' && event.job) {
          applyStreamedPrintJob(event.job);
        }
      },
      () => {
        ensurePrintJobsFallback();
      },
    );

    return () => {
      unsubscribe();
      clearPrintJobsFallback();
    };
  }, [
    applyStreamedPrintJob,
    clearPrintJobsFallback,
    employeeSession,
    ensurePrintJobsFallback,
    hasEverOpenedPrinterSettings,
    isManager,
    tenantId,
  ]);

  const resetForEmployeeSwitch = useCallback(() => {
    setSelectedTestPrinterId(null);
    setHasEverOpenedPrinterSettings(false);
    settingsInitialLoadForSessionRef.current = null;
    printersLoadedForSessionRef.current = null;
  }, []);

  return {
    selectedTestPrinterId,
    setSelectedTestPrinterId,
    loadSettingsEmployees,
    loadSettingsAreas,
    loadSettingsProducts,
    loadActivePrinters,
    loadPrintJobs,
    resetForEmployeeSwitch,
  };
}
